import { AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { Locate } from "lucide-react";
import { memo, useCallback, useEffect, useRef } from "react";
import { GoogleMapWrapper } from "@/components/map/GoogleMapWrapper";
import { SearchRadiusOverlay } from "@/components/map/SearchRadiusOverlay";
import { getCourtColor } from "@/components/map/SportCourtMarker";
import { statusColor } from "@/lib/mapping-status-colors";
import { sportTypeIcons } from "@/lib/sport-icons";
import { cn } from "@/lib/utils";

interface StatusPinProps {
  position: google.maps.LatLngLiteral;
  status: string;
  label?: string | number;
  onClick?: () => void;
  isSelected?: boolean;
  zIndex?: number;
}

// In the mapper UI, "in_progress" is shown as "Done" (green) — the
// in_progress/done distinction is only meaningful to admins later.
function mapperPinColor(status: string): string {
  if (status === "in_progress") return statusColor("done");
  return statusColor(status);
}

function StatusPin({ position, status, label, onClick, isSelected, zIndex }: StatusPinProps) {
  const color = mapperPinColor(status);
  return (
    <AdvancedMarker position={position} onClick={onClick} zIndex={zIndex}>
      <div
        className={cn(
          "relative flex flex-col items-center cursor-pointer origin-bottom transition-transform",
          isSelected ? "scale-125" : "hover:scale-110",
        )}
      >
        <svg
          width="30"
          height="42"
          viewBox="0 0 30 42"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M15 0C6.716 0 0 6.716 0 15c0 11.25 15 27 15 27s15-15.75 15-27C30 6.716 23.284 0 15 0z"
            fill={color}
            stroke="white"
            strokeWidth="1.5"
          />
          {label !== undefined && (
            <text
              x="15"
              y="19"
              textAnchor="middle"
              fill="white"
              fontSize="11"
              fontWeight="700"
              fontFamily="ui-sans-serif, system-ui, sans-serif"
            >
              {label}
            </text>
          )}
        </svg>
      </div>
    </AdvancedMarker>
  );
}

interface LocationPin {
  id: string;
  lat: number;
  lng: number;
  status: string;
}

interface CourtPin {
  id: string;
  lat: number;
  lng: number;
  sportType: string;
  radius: number;
}

interface PILPin {
  id: string;
  lat: number;
  lng: number;
  status: string;
  ordinal: number;
}

function FocusOnSelectedPil({
  pils,
  selectedPilId,
  focusZoom = 20,
}: {
  pils?: PILPin[];
  selectedPilId?: string | null;
  focusZoom?: number;
}) {
  const map = useMap();
  useEffect(() => {
    if (!map || !selectedPilId || !pils) return;
    const pil = pils.find((p) => p.id === selectedPilId);
    if (!pil) return;
    map.panTo({ lat: pil.lat, lng: pil.lng });
    const currentZoom = map.getZoom() ?? 0;
    if (currentZoom < focusZoom) map.setZoom(focusZoom);
  }, [map, selectedPilId, pils, focusZoom]);
  return null;
}

function RecenterButton({
  userPos,
  focusZoom = 16,
}: {
  userPos: { lat: number; lng: number };
  focusZoom?: number;
}) {
  const map = useMap();
  const handleClick = useCallback(() => {
    if (!map) return;
    map.panTo({ lat: userPos.lat, lng: userPos.lng });
    const currentZoom = map.getZoom() ?? 0;
    if (currentZoom < focusZoom) map.setZoom(focusZoom);
  }, [map, userPos.lat, userPos.lng, focusZoom]);
  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Center on my location"
      className="absolute right-3 top-3 z-10 flex size-10 items-center justify-center rounded-full bg-background text-foreground shadow-md transition-transform hover:scale-105 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <Locate size={18} aria-hidden="true" />
    </button>
  );
}

// Recenter on a fixed target (e.g. the PIL's saved coordinates) rather than
// the user. Used by the PILFormSheet minimap (section F).
function RecenterOnTargetButton({
  target,
  focusZoom = 20,
  label = "Recenter map",
}: {
  target: { lat: number; lng: number };
  focusZoom?: number;
  label?: string;
}) {
  const map = useMap();
  const handleClick = useCallback(() => {
    if (!map) return;
    map.panTo({ lat: target.lat, lng: target.lng });
    map.setZoom(focusZoom);
  }, [map, target.lat, target.lng, focusZoom]);
  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={label}
      className="absolute right-2 top-2 z-10 flex size-8 items-center justify-center rounded-full bg-background/95 text-foreground shadow-md backdrop-blur transition-transform hover:scale-105 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <Locate size={14} aria-hidden="true" />
    </button>
  );
}

// Tracks the map's centre and forwards it to a callback. Used by the GPS
// accuracy modal (section E) so the parent can read map.getCenter() at the
// moment "Confirm" is tapped — the saved lat/lng is the map centre, NOT the
// device's GPS at confirm time.
function CenterChangeReporter({
  onCenterChanged,
}: {
  onCenterChanged: (lat: number, lng: number) => void;
}) {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    const listener = map.addListener("idle", () => {
      const center = map.getCenter();
      if (!center) return;
      onCenterChanged(center.lat(), center.lng());
    });
    return () => listener.remove();
  }, [map, onCenterChanged]);
  return null;
}

function UserLocationMarker({
  position,
  accuracy,
}: {
  position: google.maps.LatLngLiteral;
  accuracy?: number;
}) {
  const showHalo = typeof accuracy === "number" && accuracy > 15;
  return (
    <AdvancedMarker position={position} zIndex={1100}>
      <div className="relative flex items-center justify-center">
        {showHalo && (
          <div
            className="absolute rounded-full"
            style={{
              width: 48,
              height: 48,
              backgroundColor: "rgba(26, 115, 232, 0.15)",
              border: "1px solid rgba(26, 115, 232, 0.35)",
            }}
          />
        )}
        <div
          className="rounded-full"
          style={{
            width: 14,
            height: 14,
            backgroundColor: "#1A73E8",
            border: "2px solid white",
            boxShadow: "0 1px 4px rgba(0, 0, 0, 0.35)",
          }}
        />
      </div>
    </AdvancedMarker>
  );
}

interface MapperMapProps {
  className?: string;
  defaultCenter?: google.maps.LatLngLiteral;
  defaultZoom?: number;
  locations?: LocationPin[];
  courts?: CourtPin[];
  pils?: PILPin[];
  selectedPilId?: string | null;
  userPos?: { lat: number; lng: number; accuracy?: number } | null;
  showRecenterButton?: boolean;
  /** Show a small "recenter on target" button overlaid on the map. The
   *  target is the `defaultCenter`. Used by the PIL minimap (section F). */
  showRecenterOnTarget?: boolean;
  /** When true, render a fixed crosshair pin at the centre of the map; the
   *  map pans under it. Used by the GPS accuracy modal (section E). */
  centerCrosshair?: boolean;
  /** Fires whenever the map becomes idle, with the new map centre. Used
   *  alongside `centerCrosshair` to read the final lat/lng on confirm. */
  onCenterChanged?: (lat: number, lng: number) => void;
  onLocationClick?: (id: string) => void;
  onPilClick?: (id: string) => void;
  onMapClick?: (lat: number, lng: number) => void;
}

export const MapperMap = memo(function MapperMap({
  className,
  defaultCenter,
  defaultZoom = 16,
  locations,
  courts,
  pils,
  selectedPilId,
  userPos,
  showRecenterButton,
  showRecenterOnTarget,
  centerCrosshair,
  onCenterChanged,
  onLocationClick,
  onPilClick,
  onMapClick,
}: MapperMapProps) {
  const handleMapClick = useCallback(
    (e: { detail: { latLng: google.maps.LatLngLiteral | null } }) => {
      if (onMapClick && e.detail.latLng) {
        onMapClick(e.detail.latLng.lat, e.detail.latLng.lng);
      }
    },
    [onMapClick],
  );

  // Stable ref for onCenterChanged so the listener inside CenterChangeReporter
  // doesn't re-attach on every render of the parent.
  const onCenterChangedRef = useRef(onCenterChanged);
  useEffect(() => {
    onCenterChangedRef.current = onCenterChanged;
  }, [onCenterChanged]);
  const handleCenterChanged = useCallback((lat: number, lng: number) => {
    onCenterChangedRef.current?.(lat, lng);
  }, []);

  return (
    <div className={cn("relative", className)}>
    <GoogleMapWrapper
      className="h-full w-full"
      defaultCenter={defaultCenter}
      defaultZoom={defaultZoom}
      mapTypeId="satellite"
      disableDefaultUI
      onClick={onMapClick ? handleMapClick : undefined}
    >
      <FocusOnSelectedPil pils={pils} selectedPilId={selectedPilId} />
      {locations?.map((loc) => (
        <StatusPin
          key={`loc-${loc.id}`}
          position={{ lat: loc.lat, lng: loc.lng }}
          status={loc.status}
          onClick={() => onLocationClick?.(loc.id)}
          zIndex={900}
        />
      ))}

      {courts && (
        <SearchRadiusOverlay
          courts={courts.map((c) => ({ id: c.id, lat: c.lat, lng: c.lng, radius: c.radius }))}
        />
      )}

      {courts?.map((court) => {
        const Icon = sportTypeIcons[court.sportType];
        // Sport-court icon must paint above PIL pins (zIndex 1000) and the
        // PILMarker component (zIndex 1200) so users can still see the
        // court's center even when pins cluster nearby.
        return (
          <AdvancedMarker
            key={`court-${court.id}`}
            position={{ lat: court.lat, lng: court.lng }}
            zIndex={1300}
          >
            <div
              className="h-4 w-4 rounded-full border-2 border-white shadow-md flex items-center justify-center"
              style={{ backgroundColor: getCourtColor(court.sportType) }}
            >
              {Icon && <Icon size={10} stroke={1.5} color="white" />}
            </div>
          </AdvancedMarker>
        );
      })}

      {pils?.map((pil) => (
        <StatusPin
          key={`pil-${pil.id}`}
          position={{ lat: pil.lat, lng: pil.lng }}
          status={pil.status}
          label={pil.ordinal}
          isSelected={selectedPilId === pil.id}
          onClick={() => onPilClick?.(pil.id)}
          zIndex={1000}
        />
      ))}

      {userPos && (
        <UserLocationMarker
          position={{ lat: userPos.lat, lng: userPos.lng }}
          accuracy={userPos.accuracy}
        />
      )}

      {showRecenterButton && userPos && (
        <RecenterButton userPos={{ lat: userPos.lat, lng: userPos.lng }} />
      )}

      {showRecenterOnTarget && defaultCenter && (
        <RecenterOnTargetButton target={defaultCenter} />
      )}

      {onCenterChanged && (
        <CenterChangeReporter onCenterChanged={handleCenterChanged} />
      )}
    </GoogleMapWrapper>
    {centerCrosshair && (
      <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
        <svg
          width="40"
          height="56"
          viewBox="0 0 40 56"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          style={{ transform: "translateY(-12px)" }}
        >
          <path
            d="M20 0C9 0 0 9 0 20c0 15 20 36 20 36s20-21 20-36C40 9 31 0 20 0z"
            fill="#ef4444"
            stroke="white"
            strokeWidth="2"
          />
          <circle cx="20" cy="20" r="6" fill="white" />
        </svg>
      </div>
    )}
    </div>
  );
});
